import React, { useEffect, useState } from 'react';
import { FaBalanceScale, FaFileAlt, FaExternalLinkAlt } from 'react-icons/fa';

import { apiUrl, urlWebsite } from '../../../../../config';
import api from '../../../../../services/api';
import { useModal } from '../../../../../hooks/Modal';
import { useToast } from '../../../../../hooks/Toast';
import { useSearch } from '../../../../../hooks/Search';
import {
  AvaliacaoPainel,
  AvaliacaoSecao,
  AvaliacaoSecaoTitulo,
  AvaliacaoLinha,
  AvaliacaoTexto,
  AvaliacaoNota,
  AvaliacaoPill,
  AvaliacaoLink,
  AvaliacaoCampo,
  AvaliacaoTextarea,
  AvaliacaoRodape,
  AvaliacaoBotao,
} from '../styles';

interface IAvaliarModule {
  lineData: Record<string, any>;
  projectHash: string;
}

interface IPainel {
  modalKey: string;
  id: string;
  projectHash: string;
  onDone: () => void;
}

const formatarData = (valor?: string): string => (valor ? new Date(valor).toLocaleString('pt-BR') : '—');

const Linha = ({ rotulo, valor }: { rotulo: string; valor?: React.ReactNode }): JSX.Element => (
  <AvaliacaoLinha>
    <span>{rotulo}</span>
    <span>{valor || '—'}</span>
  </AvaliacaoLinha>
);

function PainelAvaliacao({ modalKey, id, projectHash, onDone }: IPainel): JSX.Element {
  const { removeModal } = useModal();
  const { addToast } = useToast();

  const [detalhe, setDetalhe] = useState<Record<string, any> | null>(null);
  const [erroCarga, setErroCarga] = useState('');
  const [justificativa, setJustificativa] = useState('');
  const [confirmaDivergencia, setConfirmaDivergencia] = useState(false);
  const [enviando, setEnviando] = useState('');
  const [erroEnvio, setErroEnvio] = useState('');

  const url = `${apiUrl}/isention-recurse-avaliacao/${projectHash}/${id}`;

  useEffect(() => {
    api
      .get(url)
      .then(response => setDetalhe(response?.data))
      .catch(err => setErroCarga(err?.response?.data?.message || 'Não foi possível carregar a solicitação'));
  }, [url]);

  const decidir = async (decisao: string) => {
    setErroEnvio('');
    setEnviando(decisao);

    try {
      await api.post(url, { decisao, justificativa: justificativa.trim(), confirmarDivergencia: confirmaDivergencia });

      addToast({ type: 'success', title: decisao === 'Aprovado' ? 'Isenção aprovada' : 'Isenção negada' });
      removeModal(modalKey);
      onDone();
    } catch (err) {
      setErroEnvio(err?.response?.data?.message || 'Não foi possível registrar a avaliação');
      setEnviando('');
    }
  };

  if (erroCarga) {
    return (
      <AvaliacaoPainel>
        <AvaliacaoNota className="danger">
          <span>{erroCarga}</span>
        </AvaliacaoNota>
      </AvaliacaoPainel>
    );
  }

  if (!detalhe) {
    return (
      <AvaliacaoPainel>
        <AvaliacaoTexto>Carregando solicitação...</AvaliacaoTexto>
      </AvaliacaoPainel>
    );
  }

  const { recurse, venda, aprovacao, fase } = detalhe;
  const pendente = !!fase;
  const recorreu = recurse?.have_recurse === 'Sim';
  const bloqueiaAprovar = !aprovacao?.permitida || (aprovacao?.divergenciaDocumento && !confirmaDivergencia) || !!enviando;
  const classeStatus = recurse?.status === 'Aprovado' ? 'ok' : recurse?.status === 'Negado' ? 'danger' : '';

  return (
    <AvaliacaoPainel>
      <AvaliacaoSecao>
        <AvaliacaoSecaoTitulo>Candidato</AvaliacaoSecaoTitulo>
        <Linha rotulo="Nome" valor={recurse?.name} />
        <Linha rotulo="CPF" valor={recurse?.document_number} />
        <Linha rotulo="E-mail" valor={recurse?.email} />
        <Linha rotulo="Telefone" valor={recurse?.phone} />
        <Linha rotulo="Protocolo" valor={recurse?.hash} />
        {!recorreu && recurse?.recurse_send_at ? <Linha rotulo="Data de envio" valor={formatarData(recurse?.recurse_send_at)} /> : <></>}
      </AvaliacaoSecao>

      <AvaliacaoSecao>
        <AvaliacaoSecaoTitulo>Documentos</AvaliacaoSecaoTitulo>
        {recurse?.document ? (
          <AvaliacaoLink href={`${urlWebsite}/${recurse?.document}`} target="_blank" rel="noopener noreferrer">
            <FaFileAlt /> Documento enviado na solicitação <FaExternalLinkAlt size={11} />
          </AvaliacaoLink>
        ) : (
          <AvaliacaoTexto>Nenhum documento anexado.</AvaliacaoTexto>
        )}
        {aprovacao?.divergenciaDocumento && aprovacao?.documentoVenda ? (
          <AvaliacaoLink href={`${urlWebsite}/${aprovacao?.documentoVenda}`} target="_blank" rel="noopener noreferrer">
            <FaFileAlt /> Documento anexado na inscrição <FaExternalLinkAlt size={11} />
          </AvaliacaoLink>
        ) : <></>}
      </AvaliacaoSecao>

      {recorreu ? (
        <AvaliacaoSecao>
          <AvaliacaoSecaoTitulo>Recurso do candidato</AvaliacaoSecaoTitulo>
          <Linha rotulo="Negativa original" valor={recurse?.observation} />
          <Linha rotulo="Justificativa" valor={recurse?.recurse_justification} />
          <Linha rotulo="Enviado em" valor={formatarData(recurse?.recurse_send_at)} />
          {recurse?.recurse_documents ? (
            <AvaliacaoLink href={`${urlWebsite}/${recurse?.recurse_documents}`} target="_blank" rel="noopener noreferrer">
              <FaFileAlt /> Documento anexado ao recurso <FaExternalLinkAlt size={11} />
            </AvaliacaoLink>
          ) : <></>}
        </AvaliacaoSecao>
      ) : <></>}

      <AvaliacaoSecao>
        <AvaliacaoSecaoTitulo>Inscrição</AvaliacaoSecaoTitulo>
        {venda ? (
          <>
            <Linha rotulo="Categoria" valor={venda?.category} />
            <Linha rotulo="Área" valor={venda?.area} />
            <Linha rotulo="Subárea" valor={venda?.subarea} />
            <Linha rotulo="Pagamento" valor={`${venda?.payment_method_id || '—'} · ${venda?.payment_status_id || '—'}`} />
          </>
        ) : (
          <AvaliacaoTexto>Nenhuma inscrição com este protocolo no projeto.</AvaliacaoTexto>
        )}
      </AvaliacaoSecao>

      {!pendente ? (
        <>
          <AvaliacaoSecao>
            <AvaliacaoSecaoTitulo>Resultado</AvaliacaoSecaoTitulo>
            <Linha rotulo="Status" valor={<AvaliacaoPill className={classeStatus}>{recurse?.status}</AvaliacaoPill>} />
            {recurse?.recurse_evaluated_at ? (
              <>
                <Linha rotulo="Justificativa" valor={recurse?.recurse_observation} />
                <Linha rotulo="Recurso avaliado por" valor={recurse?.recurse_evaluated_by_response?.name} />
                <Linha rotulo="Avaliado em" valor={formatarData(recurse?.recurse_evaluated_at)} />
              </>
            ) : (
              <>
                <Linha rotulo="Justificativa" valor={recurse?.observation} />
                <Linha rotulo="Avaliado por" valor={recurse?.evaluated_by_response?.name} />
                <Linha rotulo="Avaliado em" valor={formatarData(recurse?.evaluated_at)} />
              </>
            )}
          </AvaliacaoSecao>
          <AvaliacaoRodape>
            <AvaliacaoBotao type="button" onClick={() => removeModal(modalKey)}>
              Fechar
            </AvaliacaoBotao>
          </AvaliacaoRodape>
        </>
      ) : (
        <>
          {!aprovacao?.permitida ? (
            <AvaliacaoNota className="danger">
              {aprovacao?.bloqueios?.map(b => (
                <span key={b.code}>{b.message}</span>
              ))}
              <span>A aprovação está bloqueada. Negar continua disponível.</span>
            </AvaliacaoNota>
          ) : <></>}

          {aprovacao?.permitida && aprovacao?.divergenciaDocumento ? (
            <AvaliacaoNota className="warn">
              <span>O documento anexado na inscrição é diferente do enviado nesta solicitação.</span>
              <label>
                <input type="checkbox" checked={confirmaDivergencia} onChange={e => setConfirmaDivergencia(e.target.checked)} />
                Conferi os dois documentos e quero aprovar mesmo assim.
              </label>
            </AvaliacaoNota>
          ) : <></>}

          {fase === 'recurso' ? (
            <AvaliacaoNota>
              <span>Avaliação do recurso. A justificativa da negativa original é preservada.</span>
            </AvaliacaoNota>
          ) : <></>}

          <AvaliacaoCampo>
            <span>Justificativa</span>
            <AvaliacaoTextarea value={justificativa} onChange={e => setJustificativa(e.target.value)} />
          </AvaliacaoCampo>

          {erroEnvio ? (
            <AvaliacaoNota className="danger">
              <span>{erroEnvio}</span>
            </AvaliacaoNota>
          ) : <></>}

          <AvaliacaoRodape>
            <AvaliacaoBotao type="button" disabled={!!enviando} onClick={() => removeModal(modalKey)}>
              Cancelar
            </AvaliacaoBotao>
            <AvaliacaoBotao type="button" className="danger" disabled={!!enviando} onClick={() => decidir('Negado')}>
              {enviando === 'Negado' ? 'Negando...' : 'Negar'}
            </AvaliacaoBotao>
            <AvaliacaoBotao type="button" className="primary" disabled={bloqueiaAprovar} onClick={() => decidir('Aprovado')}>
              {enviando === 'Aprovado' ? 'Aprovando...' : 'Aprovar e converter em cortesia'}
            </AvaliacaoBotao>
          </AvaliacaoRodape>
        </>
      )}
    </AvaliacaoPainel>
  );
}

export default function AvaliarRecurso({ lineData, projectHash }: IAvaliarModule): JSX.Element {
  const { addModalV2 } = useModal();
  const { reloadSearchAll } = useSearch();

  const emRecurso = lineData?.status === 'Negado' && lineData?.have_recurse === 'Sim' && !lineData?.recurse_evaluated_at;
  const aguardando = !lineData?.status || lineData?.status === 'Aguardando avaliação';

  const title = emRecurso ? 'Avaliar recurso' : aguardando ? 'Avaliar solicitação' : `Ver avaliação (${lineData?.status})`;

  const handleOpen = () => {
    const key = `isencao-avaliar-${lineData?._id}`;

    addModalV2({
      key,
      title: `Solicitação de isenção · ${lineData?.name || ''}`,
   
      content: <PainelAvaliacao modalKey={key} id={lineData?._id} projectHash={projectHash} onDone={() => reloadSearchAll()} />,
    });
  };

  return (
    <button className="lineIcon" title={title} type="button" onClick={() => handleOpen()}>
      <FaBalanceScale />
    </button>
  );
}
